import React from 'react';
import Reactor from 'data/reactor';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';
import Highcharts from 'highcharts';

import UserActions from 'data/user/actions';
import { Immutable } from 'nuclear-js';

import GlobalChartUtils from 'utils/chart';
import PopoverUtils from 'utils/chart-popovers';
import PopoverControls from 'utils/popover';

import goalSeriesDataPointImg from './images/GoTchart-goal-icon.png';
import goalSeriesDataPointImgUnchecked from './images/GoTchart-goal-icon-with-opacity.png';
import _isEqual from 'lodash/isEqual';
import _cloneDeep from 'lodash/cloneDeep';
import CommonValues from 'resources/common-values';
import Analytics from 'utils/analytics';

import LinkingStudyLegendIcon from 'styles/icons/linking-studies.svg';
import HelpBox from 'components/generic/help/index';
import CommonStrings from 'resources/common-strings';
import FeatureUtils from 'utils/features';
import RuntimeActions from 'data/app-runtime/actions';
import UserGetters from 'data/user/getters';

import InitialChartOptions from './initial-chart-options.json';
import TestSeriesOptions from './test-series-options.json';
import InitialPlotOptions from './initial-plot-options.json';
import XAxisSeriesOptions from './x-axis-marker-options.json';
import QuintileOptions from './quintile-options.json';
import TypicalScoreSeriesOptions from './typical-score-options.json';
import DistrictAveragesSeriesOptions from './district-averages-options.json';
import GoalSeriesOptions from './goal-series-options.json';
import ProjectionsSeriesOptions from './projections-options.json';
import TermDataPopoverOptions from './term-data-popover-options.json';
import LinkingStudyLegendHeader from './linking-study-legend-header.json';
import CollegeLinkingStudyLegend from './college-linking-study-legend.json';
import StateLinkingStudyLegend from './state-linking-study-legend.json';
import LinkingStudyOptions from './linking-study-options.json';
import NoDataOptions from './no-data-options.json';
import DisabledOptions from './disabled-options.json';
import disabledCourse from './disabled-course';
import EnabledOptions from './enabled-options.json';
import SeparatorOptions from './separator-options.json';
import DiscreteQuintileDataOptions from './discrete-quintile-data-options.json';

const seriesIndexMap = new Map();
const seriesIndexKeys = {
    testEvents: 'TEST_EVENTS',
    goalSeries: 'GOAL_SERIES',
    quintile1: 'QUINTILE1',
    quintile2: 'QUINTILE2',
    quintile3: 'QUINTILE3',
    quintile4: 'QUINTILE4',
    quintile5: 'QUINTILE5',
    quintileNoData: 'QUINTILE_NO_DATA',
    discreteQuintile1: 'DISCRETE_QUINTILE1',
    discreteQuintile2: 'DISCRETE_QUINTILE2',
    discreteQuintile3: 'DISCRETE_QUINTILE3',
    discreteQuintile4: 'DISCRETE_QUINTILE4',
    discreteQuintile5: 'DISCRETE_QUINTILE5',
    ritSeparator: 'RIT_SEPARATOR',
    typicalScore: 'TYPICAL_SCORE',
    districtAverages: 'DISTRICT_AVERAGES',
    projectionsSeries: 'PROJECTIONS_SERIES',
    districtSeparator: 'DISTRICT_SEPARATOR',
    xAxisMarkerSeries: 'X_AXIS_MARKER_SERIES',
    popoverSeries: 'POPOVER_SERIES',
    linkingStudyHeader: 'LINKING_STUDY_HEADER',
    collegeLinkingStudyHeader: (name) => {
        return 'COLLEGE_LINKING_STUDY_HEADER-' + name;
    },
    stateLinkingStudyHeader: 'STATE_LINKING_STUDY_HEADER',
    linkingStudySeparator: 'LINKING_STUDY_SEPARATOR',
    linkingStudiesIndex: 'LINKING_STUDIES_INDEX',
};

// Used to create a single instance of the print media query list (for webkit) -
// so that print event listeners can be added and removed as users scroll through the students within a class
// otherwise the number of event listeners grows causing many non-required calls to redraw charts when the user
// requests a print.
let printMediaQueryList = null;
const elementClassList = [
    '.highcharts-xaxis-labels',
    '.highcharts-yaxis-labels',
    '.highcharts-yaxis',
    '.highcharts-background',
];

export default CreateReactClass({
    displayName: 'GrowthOverTime',

    propTypes: {
        courseName: PropTypes.string,
        selectedTermHasCourseSpecificTestEvent: PropTypes.bool.isRequired,
        chartBottom: PropTypes.number,
        chartContainer: PropTypes.string.isRequired,
        chartTop: PropTypes.number,
        collegeLinkingStudySeries: PropTypes.array,
        currentStudentBid: PropTypes.string,
        discreteQuintileSeries: PropTypes.object,
        districtAveragesSeries: PropTypes.array,
        futureTermMissing: PropTypes.bool,
        growthGoalSeries: PropTypes.array,
        loaded: PropTypes.bool.isRequired,
        popoverSeries: PropTypes.array,
        projectionsSeries: PropTypes.array,
        quintileSeries: PropTypes.object,
        RITScoreSeries: PropTypes.array,
        selectedSubject: PropTypes.instanceOf(Immutable.Map),
        stateLinkingStudySeries: PropTypes.array,
        studentBid: PropTypes.string,
        subject: PropTypes.instanceOf(Immutable.Map).isRequired,
        typicalScoreSeries: PropTypes.array,
        xAxisTerms: PropTypes.array,
    },

    manageEventListenersPopoverOutsideChart(isAdd) {
        const handler = function() {
            PopoverUtils.closeCurrentPopover();
        };

        elementClassList.forEach((item) => {
            let element = document.querySelector(`.growth-over-time ${item}`);
            if (element) {
                if (isAdd) {
                    element.addEventListener('click', handler);
                } else {
                    element.removeEventListener('click', handler);
                }
            }
        });
    },

    chartHasData() {
        return (
            this.props.RITScoreSeries ||
            this.props.typicalScoreSeries ||
            this.props.districtAveragesSeries ||
            this.props.growthGoalSeries ||
            this.props.projectionsSeries ||
            this.props.quintileSeries.quintileSeriesPopulated ||
            this.props.discreteQuintileSeries
        );
    },

    /**
     * Gets the media query list for print requests.  This ensures that only one instance of the list is created.
     * @returns A media query list for print requests or null if it is not supported by the browser.
     */
    getPrintMediaQueryList() {
        if (window.matchMedia && !printMediaQueryList) {
            printMediaQueryList = window.matchMedia('print');
        }
        return printMediaQueryList;
    },

    /**
     * Adds/Removes an event listener for a print request for this student/chart.
     * @param listener The listener to add/remove.
     * @param shouldAdd Boolean indicating if the listener should be added or removed true=add, false=remove.
     */
    updateMQLPrintEventListener(listener, shouldAdd = true) {
        const mql = this.getPrintMediaQueryList();
        if (mql) {
            shouldAdd ? mql.addListener(listener) : mql.removeListener(listener);
        }
    },

    componentDidMount() {
        this.chart = this.createChart();
        if (
            this.props.currentStudentBid === this.props.studentBid &&
            this.props.subject.get('bid') === this.props.selectedSubject.get('bid')
        ) {
            this.updateChart();
        }

        this.updateMQLPrintEventListener(this.redrawChartForPrint, true); // For Webkit
        window.addEventListener('resize', this.updateChart); // For IE and Firefox
        window.addEventListener('beforeprint', this.redrawChartForPrint); // For IE and Firefox
        window.addEventListener('afterprint', this.resetChartAfterPrint);
    },

    componentDidUpdate(prevProps, prevState) {
        this.updateChart();
        const selectedPoints = this.chart.getSelectedPoints();
        selectedPoints.forEach(PopoverUtils.deselectBar);
    },

    shouldComponentUpdate(nextProps) {
        const currentStudentAndSubject =
            nextProps.studentBid === nextProps.currentStudentBid &&
            nextProps.subject.get('bid') === nextProps.selectedSubject.get('bid');

        const growthGoalAdded =
            this.props.studentBid === nextProps.studentBid &&
            !_isEqual(this.props.growthGoalSeries, nextProps.growthGoalSeries);

        const studentChanged =
            (this.props.currentStudentBid !== nextProps.currentStudentBid &&
                nextProps.currentStudentBid === nextProps.studentBid) ||
            (this.props.currentStudentBid === nextProps.currentStudentBid &&
                nextProps.currentStudentBid === nextProps.studentBid);

        const subjectChangedForStudent =
            this.props.selectedSubject.get('bid') != nextProps.selectedSubject.get('bid') &&
            nextProps.currentStudentBid === nextProps.studentBid &&
            nextProps.selectedSubject.get('bid') === this.props.subject.get('bid');

        const justLoaded = !this.props.loaded && nextProps.loaded && currentStudentAndSubject;

        return (
            (studentChanged && nextProps.subject.get('bid') === nextProps.selectedSubject.get('bid')) ||
            subjectChangedForStudent ||
            (this.props.xAxisTerms[1] !== nextProps.xAxisTerms[1] && currentStudentAndSubject) ||
            growthGoalAdded ||
            (justLoaded && currentStudentAndSubject)
        );
    },

    componentWillUnmount() {
        if (this.chart) {
            this.chart.destroy();
        }
        this.updateMQLPrintEventListener(this.redrawChartForPrint, false);
        window.removeEventListener('resize', this.updateChart);
        window.removeEventListener('afterprint', this.resetChartAfterPrint);
        window.removeEventListener('beforeprint', this.redrawChartForPrint); // For IE and Firefox
    },

    getChartBottom(series) {
        return GlobalChartUtils.findMinForSeries(series) - 10;
    },

    getChartTop(series) {
        return GlobalChartUtils.findMaxForSeries(series) + 10;
    },

    isCheckboxChecked(seriesIndex, clickEvent, chartPreferences, seriesOptions) {
        if (clickEvent && clickEvent.target.index === seriesIndex) {
            return clickEvent.type === 'legendItemClick' ? !clickEvent.target.checkbox.checked : clickEvent.checked;
        } else if (chartPreferences.get(seriesIndex)) {
            return chartPreferences.getIn([seriesIndex, 'isChecked']);
        } else {
            return seriesOptions.selected;
        }
    },

    isChartDisabled() {
        return this.props.selectedTermHasCourseSpecificTestEvent;
    },

    disableChart() {
        // When disabling the chart, one must destory then recreate due
        // to existing charts contaminating disabled charts when switching
        // between students.
        this.chart.destroy();
        this.chart = this.createChart();
        this.chart.update(disabledCourse(this.props.courseName), false);
    },

    reenableChart() {
        this.chart.update(EnabledOptions, false);
    },

    updateChart(event = null) {
        if (this.isChartDisabled()) {
            return this.disableChart();
        } else {
            this.reenableChart();
        }

        this.manageEventListenersPopoverOutsideChart(false);
        //An event may be either a window resize or a legend checkbox click
        let clickEvent = event && event.type === 'checkboxClick' ? event : null;

        if (!this.props.loaded) {
            this.chart.showLoading('Loading....');
            return;
        }

        let chartPreferences = Reactor.evaluate(UserGetters.chartPreferences);

        const quintileSeries = this.props.quintileSeries;
        if (quintileSeries) {
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.quintile1)].setData(
                quintileSeries.quintile1Data,
                false
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.quintile2)].setData(
                quintileSeries.quintile2Data,
                false
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.quintile3)].setData(
                quintileSeries.quintile3Data,
                false
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.quintile4)].setData(
                quintileSeries.quintile4Data,
                false
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.quintile5)].setData(
                quintileSeries.quintile5Data,
                false
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.quintileNoData)].setData(quintileSeries.noData, false);
        }

        if (this.props.discreteQuintileSeries) {
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.discreteQuintile1)].setData(
                this.props.discreteQuintileSeries.quintile1Data,
                false
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.discreteQuintile2)].setData(
                this.props.discreteQuintileSeries.quintile2Data,
                false
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.discreteQuintile3)].setData(
                this.props.discreteQuintileSeries.quintile3Data,
                false
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.discreteQuintile4)].setData(
                this.props.discreteQuintileSeries.quintile4Data,
                false
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.discreteQuintile5)].setData(
                this.props.discreteQuintileSeries.quintile5Data,
                false
            );
        }
        //Update Test Event Score Series
        let seriesOptions = this.chart.series[seriesIndexMap.get(seriesIndexKeys.testEvents)].options;
        let lineColor = seriesOptions.color;
        let markerLineColor = seriesOptions.marker.lineColor;
        let markerFillColor = seriesOptions.marker.fillColor;

        let isSelected = this.isCheckboxChecked(
            seriesIndexMap.get(seriesIndexKeys.testEvents),
            clickEvent,
            chartPreferences,
            seriesOptions
        );
        let opacityValue = isSelected ? '1' : '0.1';
        let className = isSelected ? 'show-series' : 'hide-series';

        // The following ensures that when deselecting the rit score, the opacity is turned down to 0.1
        // Switching to rgba for quintile colors to make it more consistent with existing code
        // and simplify opacity value assigment.  Using lodash cloneDeep to ensure the props are not modified
        // as that would be a react anti-pattern.
        const modRITScoreSeries = _cloneDeep(this.props.RITScoreSeries);

        if (modRITScoreSeries) {
            modRITScoreSeries.forEach((score) => {
                if (score.marker && score.marker.fillColor) {
                    score.marker.fillColor = score.marker.fillColor.replace(/[\d\.]+\)$/g, opacityValue + ')');
                }
            });
        }

        this.chart.series[seriesIndexMap.get(seriesIndexKeys.testEvents)].update(
            {
                name: `<span class="${className}">RIT Score: ${this.props.subject.get('name')}</span>`,
                selected: isSelected,
                data: this.props.RITScoreSeries ? this.props.RITScoreSeries : [],
                showInLegend: this.props.RITScoreSeries ? true : false,
                color: lineColor.replace(/[\d\.]+\)$/g, opacityValue + ')'),
                marker: {
                    lineColor: markerLineColor.replace(/[\d\.]+\)$/g, opacityValue + ')'),
                },
                dataLabels: {
                    style: {
                        opacity: opacityValue,
                    },
                },
            },
            false
        );
        //Update Goal Series
        seriesOptions = this.chart.series[seriesIndexMap.get(seriesIndexKeys.goalSeries)].options;
        lineColor = seriesOptions.color;
        markerLineColor = seriesOptions.marker.lineColor;
        markerFillColor = seriesOptions.marker.fillColor;

        isSelected = this.isCheckboxChecked(
            seriesIndexMap.get(seriesIndexKeys.goalSeries),
            clickEvent,
            chartPreferences,
            seriesOptions
        );

        opacityValue = isSelected ? '1' : '0.1';
        className = isSelected ? 'show-series' : 'hide-series';

        this.chart.series[seriesIndexMap.get(seriesIndexKeys.goalSeries)].update(
            {
                name: `<span class="${className}">Goal: ${this.props.subject.get('name')}</span>`,
                selected: isSelected,
                data: this.props.growthGoalSeries ? this.props.growthGoalSeries : [],
                showInLegend: this.props.growthGoalSeries ? true : false,
                color: lineColor.replace(/[\d\.]+\)$/g, opacityValue + ')'),
                marker: {
                    lineColor: markerLineColor.replace(/[\d\.]+\)$/g, opacityValue + ')'),
                    fillColor:
                        markerFillColor && markerFillColor.includes('rgba(')
                            ? markerFillColor.replace(/[\d\.]+\)$/g, opacityValue + ')')
                            : markerFillColor,
                    symbol:
                        opacityValue === '1'
                            ? 'url(' + goalSeriesDataPointImg + ')'
                            : 'url(' + goalSeriesDataPointImgUnchecked + ')',
                },
                dataLabels: {
                    style: {
                        opacity: opacityValue,
                    },
                },
            },
            false
        );

        //Add Line, if we have Test Scores or Goals
        this.chart.series[seriesIndexMap.get(seriesIndexKeys.ritSeparator)].update(
            {
                showInLegend: this.props.RITScoreSeries || this.props.growthGoalSeries ? true : false,
                legendIndex: 2,
            },
            false
        );

        //Update Typical Score Series
        seriesOptions = this.chart.series[seriesIndexMap.get(seriesIndexKeys.typicalScore)].options;
        lineColor = seriesOptions.color;
        markerLineColor = seriesOptions.marker.lineColor;
        markerFillColor = seriesOptions.marker.fillColor;

        isSelected = this.isCheckboxChecked(
            seriesIndexMap.get(seriesIndexKeys.typicalScore),
            clickEvent,
            chartPreferences,
            seriesOptions
        );

        opacityValue = isSelected ? '1' : '0.1';
        let seriesName = isSelected
            ? this.chart.series[seriesIndexMap.get(seriesIndexKeys.typicalScore)].name.replace(
                  'hide-series',
                  'show-series'
              )
            : this.chart.series[seriesIndexMap.get(seriesIndexKeys.typicalScore)].name.replace(
                  'show-series',
                  'hide-series'
              );

        this.chart.series[seriesIndexMap.get(seriesIndexKeys.typicalScore)].update(
            {
                name: seriesName,
                data: this.props.typicalScoreSeries ? this.props.typicalScoreSeries : [],
                selected: isSelected,
                showInLegend: this.props.typicalScoreSeries ? true : false,
                color: lineColor.replace(/[\d\.]+\)$/g, opacityValue + ')'),
                marker: {
                    lineColor: markerLineColor.replace(/[\d\.]+\)$/g, opacityValue + ')'),
                    fillColor:
                        markerFillColor && markerFillColor.includes('rgba(')
                            ? markerFillColor.replace(/[\d\.]+\)$/g, opacityValue + ')')
                            : markerFillColor,
                },
                dataLabels: {
                    style: {
                        opacity: opacityValue,
                    },
                },
            },
            false
        );

        //Update District Average Series
        seriesOptions = this.chart.series[seriesIndexMap.get(seriesIndexKeys.districtAverages)].options;
        lineColor = seriesOptions.color;
        markerLineColor = seriesOptions.marker.lineColor;
        markerFillColor = seriesOptions.marker.fillColor;

        isSelected = this.isCheckboxChecked(
            seriesIndexMap.get(seriesIndexKeys.districtAverages),
            clickEvent,
            chartPreferences,
            seriesOptions
        );

        opacityValue = isSelected ? '1' : '0.1';
        seriesName = isSelected
            ? this.chart.series[seriesIndexMap.get(seriesIndexKeys.districtAverages)].name.replace(
                  'hide-series',
                  'show-series'
              )
            : this.chart.series[seriesIndexMap.get(seriesIndexKeys.districtAverages)].name.replace(
                  'show-series',
                  'hide-series'
              );

        this.chart.series[seriesIndexMap.get(seriesIndexKeys.districtAverages)].update(
            {
                name: seriesName,
                data: this.props.districtAveragesSeries ? this.props.districtAveragesSeries : [],
                selected: isSelected,
                showInLegend: this.props.districtAveragesSeries ? true : false,
                color: lineColor.replace(/[\d\.]+\)$/g, opacityValue + ')'),
                marker: {
                    lineColor: markerLineColor.replace(/[\d\.]+\)$/g, opacityValue + ')'),
                    fillColor:
                        markerFillColor && markerFillColor.includes('rgba(')
                            ? markerFillColor.replace(/[\d\.]+\)$/g, opacityValue + ')')
                            : markerFillColor,
                },
                dataLabels: {
                    style: {
                        opacity: opacityValue,
                    },
                },
            },
            false
        );

        //Update Projections Series
        seriesOptions = this.chart.series[seriesIndexMap.get(seriesIndexKeys.projectionsSeries)].options;
        lineColor = seriesOptions.color;
        markerLineColor = seriesOptions.marker.lineColor;
        markerFillColor = seriesOptions.marker.fillColor;

        isSelected = this.isCheckboxChecked(
            seriesIndexMap.get(seriesIndexKeys.projectionsSeries),
            clickEvent,
            chartPreferences,
            seriesOptions
        );

        opacityValue = isSelected ? '1' : '0.1';
        className = isSelected ? 'show-series' : 'hide-series';

        this.chart.series[seriesIndexMap.get(seriesIndexKeys.projectionsSeries)].update(
            {
                name: `<span class="${className}">Projected Score: ${this.props.subject.get('name')}</span>`,
                data: this.props.projectionsSeries ? this.props.projectionsSeries : [],
                selected: isSelected,
                showInLegend: this.props.projectionsSeries ? true : false,
                color: lineColor.replace(/[\d\.]+\)$/g, opacityValue + ')'),
                marker: {
                    lineColor: markerLineColor.replace(/[\d\.]+\)$/g, opacityValue + ')'),
                    fillColor:
                        markerFillColor && markerFillColor.includes('rgba(')
                            ? markerFillColor.replace(/[\d\.]+\)$/g, opacityValue + ')')
                            : markerFillColor,
                },
                dataLabels: {
                    style: {
                        opacity: opacityValue,
                    },
                },
            },
            false
        );

        //Add a line for district data, if necessary
        this.chart.series[seriesIndexMap.get(seriesIndexKeys.districtSeparator)].update(
            {
                showInLegend:
                    this.props.typicalScoreSeries || this.props.districtAveragesSeries || this.props.projectionsSeries
                        ? true
                        : false,
                legendIndex: 6,
            },
            false
        );

        //Evaluate and Update Linking Study Series
        let linkingStudyIndex = seriesIndexMap.get(seriesIndexKeys.linkingStudiesIndex);

        //Used for data label correction
        let collegeLinkingStudyIndices = [];
        let stateLinkingStudiesIndexStart = null,
            stateLinkingStudiesIndexEnd = null,
            stateLinkingStudiesDataIndex = null;

        //Show or hide linking study header depending on if we have any linking studies
        this.chart.series[seriesIndexMap.get(seriesIndexKeys.linkingStudyHeader)].update(
            {
                marker: {
                    symbol: 'url(' + LinkingStudyLegendIcon + ')',
                },
                showInLegend: this.props.collegeLinkingStudySeries || this.props.stateLinkingStudySeries ? true : false,
            },
            false
        );

        //Clear All Linking Studies, if they exist.  We'll load fresh linking study data later.
        const linkingStudySeries = this.chart.series.slice(seriesIndexMap.get(seriesIndexKeys.linkingStudiesIndex));

        linkingStudySeries.forEach((lsSeries) => {
            lsSeries.remove(false);
        });

        let alphaValue = null;
        let textAlphaValue = null;
        let showLinkingStudyHeaderInPrint = false;

        if (this.props.collegeLinkingStudySeries) {
            GlobalChartUtils.collegeLinkingStudyTypes.forEach((lsKey) => {
                const cls = this.props.collegeLinkingStudySeries.find((item) => {
                    return item[0].stack === lsKey;
                });
                if (cls) {
                    const seriesIndex = seriesIndexMap.get(seriesIndexKeys.collegeLinkingStudyHeader(lsKey));

                    seriesOptions = this.chart.series[seriesIndex].options;
                    isSelected = this.isCheckboxChecked(seriesIndex, clickEvent, chartPreferences, seriesOptions);
                    showLinkingStudyHeaderInPrint = showLinkingStudyHeaderInPrint || isSelected;

                    alphaValue = isSelected ? '0.50' : '0.1';
                    textAlphaValue = isSelected ? '1' : '0.1';
                    className = isSelected ? 'show-series' : 'hide-series';

                    const collegeLinkingStudiesIndexStart = linkingStudyIndex;
                    let collegeLinkingStudiesDataIndex = null;
                    cls.forEach((plotPoint) => {
                        if (!this.chart.series[linkingStudyIndex]) {
                            this.chart.addSeries(LinkingStudyOptions, false);
                        }
                        plotPoint.color = plotPoint.color.replace(/[\d\.]+\)$/g, alphaValue + ')');
                        plotPoint.dataLabels.backgroundColor = plotPoint.dataLabels.backgroundColor.replace(
                            /[\d\.]+\)$/g,
                            alphaValue + ')'
                        );
                        plotPoint.dataLabels.style.opacity = textAlphaValue;
                        this.chart.series[linkingStudyIndex].update(plotPoint, false);
                        if (!collegeLinkingStudiesDataIndex) {
                            //Assumes all series data live at the same index, returns the index where y is not null
                            collegeLinkingStudiesDataIndex = plotPoint.data.findIndex((item) => item.y);
                        }
                        linkingStudyIndex++;
                    });

                    const collegeLinkingStudiesIndexEnd = linkingStudyIndex - 1;

                    collegeLinkingStudyIndices.push({
                        startIndex: collegeLinkingStudiesIndexStart,
                        dataIndex: collegeLinkingStudiesDataIndex,
                        endIndex: collegeLinkingStudiesIndexEnd,
                    });

                    this.chart.series[seriesIndex].update(
                        {
                            name: `<span class="${className}">${lsKey}</span>`,
                            selected: isSelected,
                            showInLegend: true,
                        },
                        false
                    );
                } else {
                    this.chart.series[seriesIndexMap.get(seriesIndexKeys.collegeLinkingStudyHeader(lsKey))].update(
                        {
                            showInLegend: false,
                        },
                        false
                    );
                }
            });
        } else {
            //remove all from legend
            GlobalChartUtils.collegeLinkingStudyTypes.forEach((lsKey) => {
                this.chart.series[seriesIndexMap.get(seriesIndexKeys.collegeLinkingStudyHeader(lsKey))].update(
                    {
                        showInLegend: false,
                    },
                    false
                );
            });
        }

        if (this.props.stateLinkingStudySeries) {
            seriesOptions = this.chart.series[seriesIndexMap.get(seriesIndexKeys.stateLinkingStudyHeader)].options;
            isSelected = this.isCheckboxChecked(
                seriesIndexMap.get(seriesIndexKeys.stateLinkingStudyHeader),
                clickEvent,
                chartPreferences,
                seriesOptions
            );
            showLinkingStudyHeaderInPrint = showLinkingStudyHeaderInPrint || isSelected;

            alphaValue = isSelected ? '0.50' : '0.1';
            textAlphaValue = isSelected ? '1' : '0.1';
            className = isSelected ? 'show-series' : 'hide-series';

            stateLinkingStudiesIndexStart = linkingStudyIndex;
            this.props.stateLinkingStudySeries.forEach((sls) => {
                if (!this.chart.series[linkingStudyIndex]) {
                    this.chart.addSeries(LinkingStudyOptions, false);
                }
                sls.color = sls.color.replace(/[\d\.]+\)$/g, alphaValue + ')');
                sls.dataLabels.backgroundColor = sls.dataLabels.backgroundColor.replace(
                    /[\d\.]+\)$/g,
                    alphaValue + ')'
                );
                sls.dataLabels.style.opacity = textAlphaValue;
                this.chart.series[linkingStudyIndex].update(sls, false);
                if (!stateLinkingStudiesDataIndex) {
                    //Assumes all series data live at the same index, returns the index where y is not null
                    stateLinkingStudiesDataIndex = sls.data.findIndex((item) => item.y);
                }
                linkingStudyIndex++;
            });
            stateLinkingStudiesIndexEnd = linkingStudyIndex - 1;

            this.chart.series[seriesIndexMap.get(seriesIndexKeys.stateLinkingStudyHeader)].update(
                {
                    name: `<span class="${className}">${this.props.stateLinkingStudySeries[0].stack}</span>`,
                    selected: isSelected,
                    showInLegend: true,
                },
                false
            );
        } else {
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.stateLinkingStudyHeader)].update(
                {
                    showInLegend: false,
                },
                false
            );
        }

        seriesName = showLinkingStudyHeaderInPrint
            ? this.chart.series[seriesIndexMap.get(seriesIndexKeys.linkingStudySeparator)].name.replace(
                  'hide-series',
                  'show-series'
              )
            : this.chart.series[seriesIndexMap.get(seriesIndexKeys.linkingStudySeparator)].name.replace(
                  'show-series',
                  'hide-series'
              );

        this.chart.series[seriesIndexMap.get(seriesIndexKeys.linkingStudySeparator)].update(
            {
                name: seriesName,
                showInLegend: this.props.collegeLinkingStudySeries || this.props.stateLinkingStudySeries ? true : false,
                legendIndex: 10,
            },
            false
        );

        seriesName = showLinkingStudyHeaderInPrint
            ? this.chart.series[seriesIndexMap.get(seriesIndexKeys.linkingStudyHeader)].name.replace(
                  'hide-series',
                  'show-series'
              )
            : this.chart.series[seriesIndexMap.get(seriesIndexKeys.linkingStudyHeader)].name.replace(
                  'show-series',
                  'hide-series'
              );

        this.chart.series[seriesIndexMap.get(seriesIndexKeys.linkingStudyHeader)].update(
            {
                name: seriesName,
            },
            false
        );

        this.chart.series[seriesIndexMap.get(seriesIndexKeys.popoverSeries)].setData(this.props.popoverSeries, false);

        let termsCount = 12;
        if (
            this.props.stateLinkingStudySeries &&
            this.props.stateLinkingStudySeries.find((t) => t.data[12].y != null)
        ) {
            termsCount++; //Create room to display linking studies when its last term where it needs to be displayed.
        }
        this.chart.xAxis[0].setExtremes(1, termsCount, false);

        //If the chart has any data, add the xAxis markers, labels, axes extremes, and yAxis RIT scores.
        if (this.chartHasData()) {
            this.chart.update(InitialChartOptions, false);
            this.chart.xAxis[0].setCategories(this.props.xAxisTerms, false);
            this.chart.xAxis[0].update(
                {
                    visible: true,
                },
                false
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.xAxisMarkerSeries)].setData(
                GlobalChartUtils.populateXAxisMarkerSeries(this.props.xAxisTerms.length, this.props.chartTop + 10, 9),
                false
            );

            this.chart.yAxis[0].setExtremes(this.props.chartBottom, this.props.chartTop, false);
            this.chart.yAxis[0].update(
                {
                    visible: true,
                },
                false
            );
            /* TP-268831 bugfix - Missing legend for GoT chart
             * Highcharts 7.1.3 (via upgrade of 7.1.0 -> 7.2.1) introduced bug that would
             * not update legend, leaving it 'enabled: false'.
             */
            this.chart.legend.update({ enabled: true }, false);

            //IF we have no data, remove the axes information and markers and remove any legend items
        } else {
            this.chart.update(NoDataOptions, false);
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.xAxisMarkerSeries)].setData(null, false);
        }

        //If we are resizing, we need to reflow to the container
        if (event && event.type === 'resize') {
            this.chart.reflow();
        }
        //Finally, redraw the chart just once.
        this.chart.redraw();
        this.chart.hideLoading();

        //Add formatting and click events to the data points (can't be done until data is drawn in chart)
        GlobalChartUtils.dataLabelFormatting(
            this.chart,
            seriesIndexMap.get(seriesIndexKeys.testEvents),
            this.props.chartContainer
        );
        GlobalChartUtils.linkingStudyDataLabelClick(
            this.chart,
            this.props.collegeLinkingStudySeries,
            this.props.stateLinkingStudySeries,
            seriesIndexMap.get(seriesIndexKeys.linkingStudiesIndex),
            this.props.chartContainer
        );
        // Fix overlapping data labels for linking studies (call AFTER chart.redraw()!)
        collegeLinkingStudyIndices.forEach((indices) => {
            GlobalChartUtils.fixColumnChartDataLabels(
                this.chart,
                indices.startIndex,
                indices.endIndex,
                indices.dataIndex
            );
        });
        GlobalChartUtils.fixColumnChartDataLabels(
            this.chart,
            stateLinkingStudiesIndexStart,
            stateLinkingStudiesIndexEnd,
            stateLinkingStudiesDataIndex
        );

        if (this.props.futureTermMissing) this.setMessageTopPosition(this.chart);
        this.manageEventListenersPopoverOutsideChart(true);
    },

    toggleData(clickEvent) {
        let eventSaved = Immutable.Map({
            name: clickEvent.target.name,
            className: clickEvent.target.className,
            isChecked:
                clickEvent.type === 'legendItemClick'
                    ? !clickEvent.target.checkbox.checked
                    : clickEvent.target.checkbox.checked,
        });

        UserActions.setChartPreference(clickEvent.target.index, eventSaved);

        const series = this.chart.series[clickEvent.target.index];

        const nweaData = series.options.nweaData;

        if (nweaData) {
            const analyticsActionName = nweaData.analyticsActionName;
            if (analyticsActionName) {
                Analytics.recordButtonClick(
                    CommonValues.SectionIDs.growthOverTime,
                    CommonValues.GrowthOverTimeActionNames[analyticsActionName],
                    clickEvent.target.checkbox.checked ? 'on' : 'off'
                );
            }
        }
        this.updateChart(clickEvent);
        PopoverUtils.closeCurrentPopover();
        if (clickEvent.preventDefault) {
            clickEvent.preventDefault();
        }
    },

    missingTermHelpText() {
        return (
            <div key="growth-over-time-missing-terms-help-content">
                <h3 key="growth-over-time-missing-terms-help-header">{CommonStrings.gotMissingTerms.helpBoxTitle}</h3>
                <span key="growth-over-time-missing-terms-text">{CommonStrings.gotMissingTerms.helpBoxText}</span>
            </div>
        );
    },

    renderMissingTermsMessage() {
        const missingTermsHelpIcon = (
            <HelpBox
                id={'growth-over-time-missing-terms-help-box-' + this.props.chartContainer}
                adjustOverflow={true}
                appLevelCssClassName="show-normal-growth-over-time-missing-terms-help-box"
                helpContentsRenderer={this.missingTermHelpText}
            />
        );

        if (this.chartHasData() && this.props.futureTermMissing && !this.isChartDisabled()) {
            return [
                <div
                    id={'growth-over-time-legend-message-' + this.props.chartContainer}
                    key="got-missing-terms-legend-message"
                    className="growth-over-time-legend-message"
                >
                    {CommonStrings.gotMissingTerms.labelMessage} {missingTermsHelpIcon}
                </div>,
            ];
        } else return '';
    },

    setMessageTopPosition(chart) {
        const messageArea = document.getElementById('growth-over-time-legend-message-' + this.props.chartContainer);

        if (messageArea && this.chartHasData() && this.props.loaded) {
            let visibleLegendItems = chart.series.filter((s) => s.options.showInLegend);
            visibleLegendItems.sort(
                (leftSide, rightSide) => leftSide.options.legendIndex < rightSide.options.legendIndex
            );
            let lastLegendItem = visibleLegendItems[visibleLegendItems.length - 1];
            let lastLegendItemTranslate = lastLegendItem.legendGroup ? lastLegendItem.legendGroup.translateY : null;
            visibleLegendItems.forEach(
                (item) =>
                    (lastLegendItemTranslate =
                        item.legendGroup.translateY > lastLegendItemTranslate
                            ? item.legendGroup.translateY
                            : lastLegendItemTranslate)
            );
            const labelHeight = 35;
            let topPosition =
                chart.chartHeight - chart.plotHeight + chart.plotTop + lastLegendItemTranslate + labelHeight;

            messageArea.style.top = topPosition / 10 + 'rem';
        } else if (messageArea) {
            messageArea.style.bottom = '9rem';
        }
    },

    createChart() {
        const series = [];
        const pushSeriesItem = (item, indexKey) => {
            // push the item onto the series array, and save the index in the seriesIndexMap
            // so we can retrieve it via it's key later.
            seriesIndexMap.set(indexKey, series.length);
            series.push(item);
        };

        const popoverEvents = {
            select: PopoverUtils.togglePopover,
            unselect: PopoverUtils.togglePopover,
        };
        // The order in which these series are added is important as some are tied to css
        // via the index they are given within the chart (as noted below)
        seriesIndexMap.clear();
        pushSeriesItem(GlobalChartUtils.createDataSeries(TestSeriesOptions), seriesIndexKeys.testEvents);
        pushSeriesItem(GlobalChartUtils.createDataSeries(GoalSeriesOptions), seriesIndexKeys.goalSeries);
        pushSeriesItem(QuintileOptions.quintile1, seriesIndexKeys.quintile1);
        pushSeriesItem(QuintileOptions.quintile2, seriesIndexKeys.quintile2);
        pushSeriesItem(QuintileOptions.quintile3, seriesIndexKeys.quintile3);
        pushSeriesItem(QuintileOptions.quintile4, seriesIndexKeys.quintile4);
        // series 6 has css tied to it in app/components/student-profile/modules/print.less
        pushSeriesItem(QuintileOptions.quintile5, seriesIndexKeys.quintile5);
        pushSeriesItem(QuintileOptions.noData, seriesIndexKeys.quintileNoData);
        pushSeriesItem(DiscreteQuintileDataOptions.discreteQuintile1, seriesIndexKeys.discreteQuintile1);
        pushSeriesItem(DiscreteQuintileDataOptions.discreteQuintile2, seriesIndexKeys.discreteQuintile2);
        pushSeriesItem(DiscreteQuintileDataOptions.discreteQuintile3, seriesIndexKeys.discreteQuintile3);
        pushSeriesItem(DiscreteQuintileDataOptions.discreteQuintile4, seriesIndexKeys.discreteQuintile4);
        pushSeriesItem(DiscreteQuintileDataOptions.discreteQuintile5, seriesIndexKeys.discreteQuintile5);
        pushSeriesItem(GlobalChartUtils.createDataSeries(SeparatorOptions), seriesIndexKeys.ritSeparator);
        pushSeriesItem(GlobalChartUtils.createDataSeries(TypicalScoreSeriesOptions), seriesIndexKeys.typicalScore);
        pushSeriesItem(
            GlobalChartUtils.createDataSeries(DistrictAveragesSeriesOptions),
            seriesIndexKeys.districtAverages
        );
        pushSeriesItem(GlobalChartUtils.createDataSeries(ProjectionsSeriesOptions), seriesIndexKeys.projectionsSeries);
        pushSeriesItem(GlobalChartUtils.createDataSeries(SeparatorOptions), seriesIndexKeys.districtSeparator);
        pushSeriesItem(GlobalChartUtils.createDataSeries(XAxisSeriesOptions), seriesIndexKeys.xAxisMarkerSeries);
        pushSeriesItem(
            GlobalChartUtils.createDataSeries(TermDataPopoverOptions, popoverEvents),
            seriesIndexKeys.popoverSeries
        );
        // growth-over-time.less contains css for LINKING_STUDY_HEADER (series 20)
        // tied to the order of these items: ".highcharts-series-[index] image"
        pushSeriesItem(GlobalChartUtils.createDataSeries(LinkingStudyLegendHeader), seriesIndexKeys.linkingStudyHeader);
        GlobalChartUtils.collegeLinkingStudyTypes.forEach((linkingStudy) => {
            pushSeriesItem(
                GlobalChartUtils.createDataSeries(CollegeLinkingStudyLegend),
                seriesIndexKeys.collegeLinkingStudyHeader(linkingStudy)
            );
        });
        pushSeriesItem(
            GlobalChartUtils.createDataSeries(StateLinkingStudyLegend),
            seriesIndexKeys.stateLinkingStudyHeader
        );
        pushSeriesItem(GlobalChartUtils.createDataSeries(SeparatorOptions), seriesIndexKeys.linkingStudySeparator);
        //Don't push any linking studies yet, but save the slot where they will start
        seriesIndexMap.set(seriesIndexKeys.linkingStudiesIndex, series.length);
        return Highcharts.chart(this.props.chartContainer, {
            ...InitialChartOptions,
            series,
            chart: {
                events: {
                    click: PopoverUtils.clearPopovers,
                },
            },
            plotOptions: {
                ...InitialPlotOptions,
                series: {
                    events: {
                        checkboxClick: (event) => {
                            {
                                this.toggleData(event);
                            }
                        },
                        legendItemClick: (event) => {
                            if (
                                event.target.index === seriesIndexMap.get(seriesIndexKeys.linkingStudyHeader) ||
                                event.target.index === seriesIndexMap.get(seriesIndexKeys.ritSeparator) ||
                                event.target.index === seriesIndexMap.get(seriesIndexKeys.districtSeparator) ||
                                event.target.index === seriesIndexMap.get(seriesIndexKeys.linkingStudySeparator)
                            ) {
                                return false;
                            } else {
                                {
                                    this.toggleData(event);
                                }
                            }
                        },
                    },
                },
            },
        });
    },

    redrawChartForPrint(eventOrMQL) {
        if (this.chart.series) {
            //The chart has initialized so carry on with the redraw
            let chartPreferences = Reactor.evaluate(UserGetters.chartPreferences);
            let isMQL = false;
            let isPrintRequest = false;
            if (eventOrMQL.matches) {
                isPrintRequest = eventOrMQL.matches;
                isMQL = true;
            } else {
                isPrintRequest = eventOrMQL.type == 'beforeprint';
            }
            let showLinkingStudyHeaderInPrint = !isPrintRequest; // Always show when media is screen
            let seriesOptions = null;
            let isSelected = false;

            if (this.props.collegeLinkingStudySeries && isPrintRequest) {
                try {
                    for (let i = 0; i < GlobalChartUtils.collegeLinkingStudyTypes.length; i++) {
                        const seriesIndex = seriesIndexMap.get(
                            seriesIndexKeys.collegeLinkingStudyHeader(GlobalChartUtils.collegeLinkingStudyTypes[i])
                        );
                        seriesOptions = this.chart.series[seriesIndex].options;
                        showLinkingStudyHeaderInPrint = this.isCheckboxChecked(
                            seriesIndex,
                            null,
                            chartPreferences,
                            seriesOptions
                        );
                        if (showLinkingStudyHeaderInPrint) {
                            break;
                        }
                    }
                } catch (e) {
                    // If this isn't the current student, the chart object won't have a series, and throws an error.
                    // Just catch it and proceed to the next.
                }
            }
            if (this.props.stateLinkingStudySeries && isPrintRequest && !showLinkingStudyHeaderInPrint) {
                try {
                    seriesOptions = this.chart.series[seriesIndexMap.get(seriesIndexKeys.stateLinkingStudyHeader)]
                        .options;
                    isSelected = this.isCheckboxChecked(
                        seriesIndexMap.get(seriesIndexKeys.stateLinkingStudyHeader),
                        null,
                        chartPreferences,
                        seriesOptions
                    );
                    showLinkingStudyHeaderInPrint = showLinkingStudyHeaderInPrint || isSelected;
                } catch (e) {
                    // If this isn't the current student, the chart object won't have a series, and throws an error.
                    // Just catch it and proceed to the next.
                }
            }
            const legendSymbol = showLinkingStudyHeaderInPrint ? 'url(' + LinkingStudyLegendIcon + ')' : null;

            this.chart.series[seriesIndexMap.get(seriesIndexKeys.linkingStudyHeader)].update(
                {
                    marker: {
                        symbol: legendSymbol,
                    },
                },
                false
            );

            if (!isMQL && isPrintRequest) {
                const width = FeatureUtils.isIE() || FeatureUtils.isEdge() ? 640 : 740;
                this.chart.setSize(width, 400, false);
            }
            if (FeatureUtils.isSafari()) {
                const width = isPrintRequest ? 640 : null;
                this.chart.setSize(width, 400, false);
            }
            this.chart.reflow();
            // adjust data point sizing
            GlobalChartUtils.dataLabelFormatting(
                this.chart,
                seriesIndexMap.get(seriesIndexKeys.testEvents),
                this.props.chartContainer,
                false,
                0.75
            );
        }
    },

    resetChartAfterPrint(event) {
        this.chart.setSize(null, 400, false);
        this.chart.reflow();
        // re-establish data point sizing
        GlobalChartUtils.dataLabelFormatting(
            this.chart,
            seriesIndexMap.get(seriesIndexKeys.testEvents),
            this.props.chartContainer,
            false
        );
    },

    renderPercentileBands() {
        if (this.chartHasData() && this.props.loaded && !this.isChartDisabled()) {
            return (
                <ul className="quintile-legend">
                    <li key="title" className="title">
                        Percentile Bands
                    </li>
                    <li key="quintile1" className="quintile1">
                        1-20
                    </li>
                    <li key="quintile2" className="quintile2">
                        21-40
                    </li>
                    <li key="quintile3" className="quintile3">
                        41-60
                    </li>
                    <li key="quintile4" className="quintile4">
                        61-80
                    </li>
                    <li key="quintile5" className="quintile5">
                        81-100
                    </li>
                    <li key="no-data" className="no-data">
                        no data
                    </li>
                </ul>
            );
        } else {
            return null;
        }
    },

    handleClickEventForSetGoal(event) {
        if (
            event &&
            event.target &&
            event.target.dataset &&
            event.target.dataset.term &&
            event.target.id === 'setGoal' + event.target.dataset.term
        ) {
            RuntimeActions.expandModule('growth-goals', parseInt(event.target.dataset.term));
            Analytics.recordAction(
                CommonValues.SectionIDs.growthOverTime,
                CommonValues.GrowthOverTimeActionNames.setGoalPopover
            );
        }
        PopoverControls.closeOpenPopovers('');
        event.stopPropagation(); // Prevents page event listener from closing popover
    },

    /* WARNING - Non intuitive functionality
  Based on the assumption that the GOT chart is the last module to transition when transitioning out modules,
  we can execute the completion of a given transtition on the transitionEnd of this element and begin
  the process of moving the data to the correct stores.
*/
    onTransitionEnd(event) {
        event.stopPropagation();
        RuntimeActions.completeTransition();
    },

    onAnimationEnd(event) {
        event.stopPropagation();
        Reactor.batch(() => {
            RuntimeActions.completeTransition();
            RuntimeActions.setAnimationIsRunning(false);
        });
    },

    helpText() {
        const helpImg = require('./images/sprofile_growthgraph.png');
        return (
            <div key="growth-over-time-help'">
                <p key="growth-over-time-content">
                    <span key="start">{CommonStrings.gotHelp.paragraph1Text}</span>
                    <strong key="term">{CommonStrings.gotHelp.paragraph2Text}</strong>
                    <span key="end">{CommonStrings.gotHelp.paragraph3Text}</span>
                    <img src={helpImg} key="gotGraph" />
                </p>
                <div key="help-link-section" className="help-link">
                    <strong key="help-read-more">{CommonStrings.gotHelp.readMoreText}</strong>
                    <a key="help-link" href={CommonStrings.gotHelp.helpLink} target="_blank">
                        {CommonStrings.gotHelp.helpText}
                    </a>
                </div>
            </div>
        );
    },

    render() {
        const percentileBands = this.renderPercentileBands();
        const missingTermsMessage = this.renderMissingTermsMessage();
        const container = this.props.chartContainer;
        return (
            <div
                className="module growth-over-time"
                key="growth-over-time"
                onTransitionEnd={this.onTransitionEnd}
                onAnimationEnd={this.onAnimationEnd}
            >
                <header>Growth Over Time</header>
                <HelpBox
                    id={'got-help-box-' + this.props.studentBid}
                    appLevelCssClassName="show-normal-got-help-box"
                    helpContentsRenderer={this.helpText}
                    moduleName={CommonValues.SectionIDs.growthOverTime}
                    helpLabel="growth-over-time-default-help"
                />
                <div
                    id={container}
                    key="growth-over-time-chart"
                    onClick={(event) => {
                        this.handleClickEventForSetGoal(event);
                    }}
                ></div>
                {missingTermsMessage}
                {percentileBands}
            </div>
        );
    },
});
